// Algorytmiczna mapa kolorow per skrot litologii. Uzywane jako fallback kiedy:
// - layer.color nie jest ustawiony manualnie
// - layer.geotechnicalLayer nie ma wpisu w catNrGeologyLayer.decodeColor
//
// Klasyfikacja zgodna z Interpolacja_2d.md (skala / zwietrzelina / grunt) plus
// podkategorie czesto wystepujace w danych klientow.

const COLORS = {
  // === SKALA (szarosci) ===
  SM: '#9CA3AF',           // skala miekka — szary
  ST: '#6B7280',           // skala twarda — ciemny szary
  Brekacja: '#475569',     // brekacja — bardzo ciemny szary

  // === ZWIETRZELINA (zoltobrazowe) ===
  KR: '#DAA520',           // zwietrzelina rumoszowa — goldenrod
  KRg: '#CD8500',          // zwietrzelina rumoszowo-gruntowa — ciemniejszy zolty
  KW: '#E8B57E',           // zwietrzelina — jasny brazowy
  KWg: '#D2A170',          // zwietrzelina gruntowa — brazowy

  // === GRUNTY (brazowe / pomarancze / oliwki) ===
  Gb: '#8B6914',           // glina brunatna — ciemny brazowy
  'Gπ': '#A0522D',         // glina pylasta — sienna
  'Gπz': '#8B4513',        // glina pylasta zwietrzala — saddle brown
  Gπp: '#A0522D',
  GpΠ: '#A0522D',
  'Iπ': '#9C7B5C',         // ił pylasty — mocca
  Pr: '#F4A460',           // piasek gruby — sandy brown
  Pd: '#FFD580',           // piasek drobny — jasny pomarancz
  Ps: '#F0C870',           // piasek sredni — zoltawy
  Pg: '#FFE7BA',           // piasek gliniasty — bardzo jasny
  Po: '#FFC380',           // piasek z otoczakami
  Nasyp: '#A98E5C',        // nasyp — oliwkowy brazowy
  nN: '#9B9275',           // nasyp niekontrolowany
  Ł: '#778899',            // lupek — szaro-niebieski
  'Łi': '#708090',         // lupek ilasty — slate gray
  Pc: '#D2B48C',           // piaskowiec — tan
};

/**
 * Zwraca kolor (#hex) dla danego short formu litologii, np. "Pd", "SM(Łi)", "Gπ".
 * Dla compositow w trybie Skala (np. "SM(Łi)") bierze pierwszy segment przed "(".
 * Dla nieznanych skrotow zwraca null (zeby wywolujacy mial szanse spadnac dalej).
 */
export function getLithologyColor(shortForm) {
  if (!shortForm) return null;
  // Wytnij ewentualna podkategorie w nawiasie ("SM(Łi)" -> "SM")
  const base = shortForm.split('(')[0].trim();
  if (COLORS[base]) return COLORS[base];
  // Fallback: dopasuj po prefixie (np. "Pdz" -> "Pd", "Gπz" -> "Gπ")
  const prefixMatch = Object.keys(COLORS).find(k => base.startsWith(k));
  return prefixMatch ? COLORS[prefixMatch] : null;
}

/**
 * Wybor koloru warstwy wg priorytetow z INSTRUKCJA_WDROZENIE_LITOLOGIA sekcja 6a.5:
 * 1. layer.customColor (manualnie ustawiony) — przekazany jako manualColor
 * 2. dictionaryColors.get(numberGeologyLayer) — z catNrGeologyLayer
 * 3. algorytmiczna mapa po lithologyRockSoil01
 * 4. legacy / domyslny szary
 *
 * Zwraca RAZEM z informacja o zrodle — potrzebna np. w trybie laczenia
 * 'bySoilGroup', ktory nadpisuje kolor grupy TYLKO wtedy, gdy warstwa nie ma
 * koloru z numeru warstwy geotechnicznej ani ustawionego recznie.
 *
 * @returns {{ color: string, source: 'manual'|'geotechnical'|'lithology'|'default' }}
 */
export function resolveLayerColorWithSource({ manualColor, numberGeologyLayer, lithologyRockSoil01, dictionaryColors = {}, fallback = '#CCCCCC' }) {
  if (manualColor) return { color: manualColor, source: 'manual' };
  if (numberGeologyLayer && dictionaryColors[numberGeologyLayer]) {
    return { color: dictionaryColors[numberGeologyLayer], source: 'geotechnical' };
  }
  const algo = getLithologyColor(lithologyRockSoil01);
  if (algo) return { color: algo, source: 'lithology' };
  return { color: fallback, source: 'default' };
}

/**
 * Wariant zwracajacy sam kolor — zachowany dla dotychczasowych wywolan.
 * Priorytety liczone w jednym miejscu (resolveLayerColorWithSource), zeby
 * obie sciezki nie mogly sie rozjechac.
 */
export function resolveLayerColor(args) {
  return resolveLayerColorWithSource(args).color;
}

/**
 * Czy kolor warstwy jest "swiadomie ustawiony" — recznie albo przez numer warstwy
 * geotechnicznej. Kolor z algorytmicznej palety litologii i szary domyslny NIE sa
 * swiadome: to zgadywanka, ktora tryb 'bySoilGroup' moze nadpisac kolorem grupy.
 */
export function hasExplicitColor(colorSource) {
  return colorSource === 'manual' || colorSource === 'geotechnical';
}

/**
 * Czy warstwa ma wlasny, swiadomie ustawiony kolor (reczny albo z numeru warstwy
 * geotechnicznej) — czy tylko zgadywanke z palety litologii.
 *
 * `colorSource` zapisujemy przy imporcie otworu. Warstwy zaimportowane przed
 * wprowadzeniem tego pola go nie maja — dla nich fallback: jesli warstwa ma
 * przypisany numer warstwy geotechnicznej, zakladamy ze kolor stamtad pochodzi.
 */
export function layerHasOwnColor(layer) {
  if (!layer) return false;
  if (layer.colorSource !== undefined) return hasExplicitColor(layer.colorSource);
  return (layer.geotechnicalLayer || '').toString().trim() !== '';
}
